import { Widget, WidgetSize } from '@automation-suite/shared-clients-widget-variant';
import { type MessageConsumer, type MessageProducer } from '@automation-suite/shared-clients-window-connection';
import { logger } from '@automation-suite/shared-clients-widget-logger';

import {
  defaultIframeStyles,
  getDesktopDefaultStyles,
  getDesktopSupportWindowStyles,
  getMobileDefaultStyles,
  getMobileSupportWindowStyles,
  GetStylesParams,
} from './consts';
import {
  type Configs,
  type HostToIframeMessage,
  type IframeToHostMessage,
  type Options,
  ActionMethod,
  type Actions,
  type DisplayModes,
  ShowActionDisplayModeSchema,
  type TransitionMap,
} from './types';
import { getDisplayModeToResponsiveStyles } from './utils';
import { createTransitionMap } from './utils';

export class WidgetVariantFloatingActionButton extends Widget<
  Actions,
  Configs,
  Options,
  MessageConsumer<IframeToHostMessage>,
  MessageProducer<HostToIframeMessage>
> {
  private previouslyVisibleDisplayMode: DisplayModes | undefined;
  private _currentDisplayMode: DisplayModes = 'HIDDEN';
  private processingQueue = false;
  private transitionQueue: (() => Promise<void>)[] = [];
  private transitionMap: TransitionMap | undefined;

  get currentDisplayMode() {
    return this._currentDisplayMode;
  }

  set currentDisplayMode(value: DisplayModes) {
    if (this._currentDisplayMode !== 'HIDDEN') {
      this.previouslyVisibleDisplayMode = this._currentDisplayMode;
    }
    this._currentDisplayMode = value;
  }

  public getIframeStyles(isDesktop: boolean): Partial<CSSStyleDeclaration> {
    return isDesktop
      ? this.displayModeToResponsiveStyles[this.currentDisplayMode].desktop
      : this.displayModeToResponsiveStyles[this.currentDisplayMode].mobile;
  }

  public async beforeMount() {
    this.iframeToHostMessageConsumer?.addListener('transition-to-fab', () => this.transitionToDisplayMode('FAB'));
    this.iframeToHostMessageConsumer?.addListener('transition-to-hidden', () => this.transitionToDisplayMode('HIDDEN'));
    this.iframeToHostMessageConsumer?.addListener('transition-to-support-window', () =>
      this.transitionToDisplayMode('WINDOW')
    );
  }

  public async afterMount() {
    this.transitionMap = createTransitionMap({
      desktopDefaultStyles: this.desktopDefaultStyles,
      mobileDefaultStyles: this.mobileDefaultStyles,
      desktopSupportWindowStyles: this.desktopSupportWindowStyles,
      mobileSupportWindowStyles: this.mobileSupportWindowStyles,
    });
    if (this.iframeElement) {
      this.applyStyles(
        this.isDesktopMediaQuery.matches
          ? this.displayModeToResponsiveStyles[this.currentDisplayMode].desktop
          : this.displayModeToResponsiveStyles[this.currentDisplayMode].mobile
      );
    }
    await this.handleFloatingActionButtonTrigger();
  }

  public async destroy() {
    this.currentDisplayMode = 'HIDDEN';
    this.processingQueue = false;
    this.transitionQueue = [];
    this.transitionMap = undefined;
    await super.destroy();
  }

  protected actions: Actions = {
    ...this._actions,
    [ActionMethod.Hide]: () => {
      this.transitionToDisplayMode('HIDDEN');
    },
    [ActionMethod.Show]: async (params) => {
      if (!params || !params.displayMode) {
        if (this.currentDisplayMode === 'HIDDEN') {
          await this.transitionToPreviouslyVisibleDisplayMode();
        }
      } else {
        const { data: parsedDisplayMode, success, error } = ShowActionDisplayModeSchema.safeParse(params.displayMode);
        if (!success) {
          logger.warn('Invalid display mode provided. No action taken.', {
            displayMode: params.displayMode,
            parseIssues: error?.issues.map((issue) => issue.message),
          });
          return;
        }

        await this.transitionToDisplayMode(parsedDisplayMode);
      }
    },
    [ActionMethod.InitiateChat]: ({ message, context } = {}) => {
      this.transitionToDisplayMode('WINDOW');
      this.hostToIframeMessageProducer?.sendMessage({
        payload: {
          message,
          context,
        },
        type: 'simplr-initiate-chat',
      });
    },
    [ActionMethod.InitiateSimplrChat]: (params = {}) => {
      logger.warn(
        `'${ActionMethod.InitiateSimplrChat}' will be deprecated. Use "${ActionMethod.InitiateChat}" instead.`
      );
      this.actions[ActionMethod.InitiateChat](params);
    },
  };

  private async handleInitialTransition() {
    const fabOptions = this.options?.['floating-action-button'];
    const initialMode = fabOptions?.initialDisplayMode ?? 'FAB';

    await this.transitionToDisplayMode(initialMode);
  }

  private async handleFloatingActionButtonTrigger() {
    const fabOptions = this.options?.['floating-action-button'];
    if (fabOptions?.trigger === 'delay' && fabOptions.delayTimeInMs) {
      await this.handleDelayTrigger(fabOptions.delayTimeInMs);
    } else if (fabOptions?.trigger === 'scroll' && fabOptions.scrollThreshold) {
      await this.handleScrollTrigger(fabOptions.scrollThreshold);
    } else {
      await this.handleInitialTransition();
    }
  }

  private async handleDelayTrigger(delayTimeInMs: number) {
    setTimeout(async () => {
      await this.handleInitialTransition();
    }, delayTimeInMs);
  }

  private async handleScrollTrigger(scrollThreshold: number) {
    const checkScrollThreshold = () => {
      const scrollTop = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      const scrolledPercentage = (scrollTop + windowHeight) / documentHeight;

      return scrolledPercentage >= scrollThreshold / 100;
    };

    if (checkScrollThreshold()) {
      await this.handleInitialTransition();
    } else {
      const handleScroll = async () => {
        if (checkScrollThreshold()) {
          await this.handleInitialTransition();
          window.removeEventListener('scroll', handleScroll);
        }
      };

      window.addEventListener('scroll', handleScroll);
    }
  }

  private applyStyles(styles: Record<string, unknown>) {
    if (this.iframeElement) {
      Object.assign(this.iframeElement.style, defaultIframeStyles);
      Object.assign(this.iframeElement.style, styles);
    }
  }

  private async asyncSendAndAwaitMessage(sendMessage: HostToIframeMessage, awaitMessage: IframeToHostMessage) {
    const listener = this.iframeToHostMessageConsumer?.addAsyncListener(awaitMessage.type);
    this.hostToIframeMessageProducer?.sendMessage(sendMessage);
    await listener;
  }

  private enqueueTransitionFunction(transitionFunction: () => Promise<void>) {
    this.transitionQueue.push(transitionFunction);
    if (!this.processingQueue) {
      this.startQueueProcessor();
    }
  }
  private async startQueueProcessor() {
    this.processingQueue = true;
    while (this.transitionQueue.length > 0) {
      const transitionFunction = this.transitionQueue.shift();
      if (transitionFunction) {
        await transitionFunction();
      }
    }
    this.processingQueue = false;
  }

  private async transitionToDisplayMode(toMode: DisplayModes) {
    this.enqueueTransitionFunction(async () => {
      if (this.currentDisplayMode === toMode) {
        return;
      }

      const isMobile = !this.isDesktopMediaQuery.matches;
      const shouldPreventScroll = toMode === 'WINDOW' && isMobile;

      if (shouldPreventScroll) {
        this.preventParentScroll();
      } else {
        this.allowParentScroll();
      }

      if (!this.transitionMap) {
        const error = new Error('Transition map is not defined. Ensure the widget is initialized properly.');
        logger.error(error);
        throw error;
      }

      const fromTransition = this.transitionMap[this.currentDisplayMode];
      const toTransition = this.transitionMap[this.currentDisplayMode]?.[toMode];

      if (fromTransition && toTransition) {
        if (this.currentDisplayMode !== 'HIDDEN') {
          await this.asyncSendAndAwaitMessage(
            fromTransition.hide.startTransitionMessageType,
            fromTransition.hide.completeTransitionMessageType
          );
        }

        this.applyStyles(toTransition.styles(this.isDesktopMediaQuery.matches));
        await this.asyncSendAndAwaitMessage(
          toTransition.show.startTransitionMessageType,
          toTransition.show.completeTransitionMessageType
        );

        this.currentDisplayMode = toMode;
      } else {
        logger.warn(`No transition defined from ${this.currentDisplayMode} to ${toMode}`);
      }
    });
  }

  private async transitionToPreviouslyVisibleDisplayMode() {
    if (this.previouslyVisibleDisplayMode) {
      await this.transitionToDisplayMode(this.previouslyVisibleDisplayMode);
    } else {
      await this.transitionToDisplayMode('FAB');
    }
  }

  private getFabStyles(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getStylesFn: (params: GetStylesParams) => Record<string, any>,
    isDesktop = false,
    size: WidgetSize = 'large'
  ) {
    const fabOptions = this.options?.['floating-action-button'];
    const position = isDesktop ? fabOptions?.position?.desktop : fabOptions?.position?.mobile;

    const offsetBottomPx = position?.offsetBottomPx ?? 0;
    const offsetRightPx = position?.offsetRightPx ?? 0;

    return getStylesFn({ offsetBottomPx, offsetRightPx, size });
  }

  private get displayModeToResponsiveStyles() {
    const size =
      this.options?.['floating-action-button']?.size ??
      this.optionOverrides?.['floating-action-button']?.size ??
      'large';
    return this.getFabStyles(getDisplayModeToResponsiveStyles, this.isDesktopMediaQuery.matches, size);
  }

  private get desktopSupportWindowStyles() {
    const size =
      this.options?.['floating-action-button']?.size ??
      this.optionOverrides?.['floating-action-button']?.size ??
      'large';
    return this.getFabStyles(getDesktopSupportWindowStyles, true, size);
  }

  private get desktopDefaultStyles() {
    return this.getFabStyles(getDesktopDefaultStyles, true);
  }

  private get mobileSupportWindowStyles() {
    return this.getFabStyles(getMobileSupportWindowStyles);
  }

  private get mobileDefaultStyles() {
    return this.getFabStyles(getMobileDefaultStyles);
  }

  protected getIframeUrl() {
    return process.env.NX_PUBLIC_WIDGET_USE_SAME_ORIGIN
      ? this.buildUrl(this.loaderUrl.origin, 'widget-variant-fab')
      : this.buildUrl(process.env.NX_PUBLIC_FAB_IFRAME_ORIGIN as string);
  }
}
